'use client';
import React from 'react';
import Link from 'next/link';
import MarketingNav from '@/app/marketing/components/MarketingNav';
import MarketingFooter from '@/app/marketing/components/MarketingFooter';

const SOLUTIONS = [
  {
    id: 'retailers',
    icon: '🏪',
    title: 'For Retailers',
    tagline: 'Showroom-ready inventory management with client-facing tools.',
    color: 'text-violet-400',
    border: 'border-violet-500/30',
    bg: 'from-violet-500/8',
    challenges: [
      'Clients ask "do you have a 1.5ct round E VS1?" — and you can\'t answer instantly',
      'Stones go on hold or memo and get forgotten',
      'Pricing is inconsistent across staff',
      'No record of what clients have looked at or want',
    ],
    solutions: [
      {
        icon: '🔍',
        title: 'Instant inventory search',
        desc: 'Answer any client query in seconds with full 4C filtering.',
      },
      {
        icon: '📋',
        title: 'Hold & memo tracking',
        desc: 'Every stone has a real-time status — available, on hold, on memo, sold.',
      },
      {
        icon: '💰',
        title: 'Consistent pricing',
        desc: 'Markup rules ensure every staff member quotes the same price.',
      },
      {
        icon: '❤️',
        title: 'Client wishlists',
        desc: 'Record what each client wants and notify them when it arrives.',
      },
    ],
    cta: 'See Retailer Features',
    href: '/features#inventory',
  },
  {
    id: 'wholesalers',
    icon: '🏭',
    title: 'For Wholesalers',
    tagline: 'High-volume memo management and supplier payables at scale.',
    color: 'text-amber-400',
    border: 'border-amber-500/30',
    bg: 'from-amber-500/8',
    challenges: [
      'Dozens of memos out at once — hard to track who has what',
      'Supplier invoices and payables scattered across emails',
      'Bulk pricing calculations take hours in spreadsheets',
      'No visibility into which clients are actually buying',
    ],
    solutions: [
      {
        icon: '📦',
        title: 'Bulk memo management',
        desc: 'Track all outgoing memos with aging alerts and return deadlines.',
      },
      {
        icon: '💳',
        title: 'Supplier payables',
        desc: "Manage what you owe, when it's due, and payment history.",
      },
      {
        icon: '⚡',
        title: 'Bulk pricing engine',
        desc: 'Price 500 stones in minutes with Rapaport + custom markup rules.',
      },
      {
        icon: '📊',
        title: 'Client buy rate analytics',
        desc: 'See which clients convert memos to sales and focus your effort.',
      },
    ],
    cta: 'See Wholesaler Features',
    href: '/features#crm',
  },
  {
    id: 'appraisers',
    icon: '🔬',
    title: 'For Appraisers',
    tagline: 'GIA-linked appraisal reports with full compliance audit trail.',
    color: 'text-emerald-400',
    border: 'border-emerald-500/30',
    bg: 'from-emerald-500/8',
    challenges: [
      'Appraisal reports are created in Word and hard to archive',
      'GIA certificate verification is manual and slow',
      'No audit trail for regulatory compliance',
      "Clients ask for historical valuations and you can't find them",
    ],
    solutions: [
      {
        icon: '📄',
        title: 'Professional report builder',
        desc: 'Create branded appraisal reports with digital signature in minutes.',
      },
      {
        icon: '✅',
        title: 'Instant GIA verification',
        desc: 'Verify certificate authenticity with one click via GIA API.',
      },
      {
        icon: '🔒',
        title: 'Compliance audit log',
        desc: 'Immutable record of every appraisal action for regulatory review.',
      },
      {
        icon: '🗄️',
        title: 'Certificate archive',
        desc: 'Search and retrieve any historical appraisal instantly.',
      },
    ],
    cta: 'See Appraiser Features',
    href: '/features#appraisal',
  },
  {
    id: 'multi-location',
    icon: '🌐',
    title: 'Multi-Location',
    tagline: 'Unified inventory and team management across all your branches.',
    color: 'text-indigo-400',
    border: 'border-indigo-500/30',
    bg: 'from-indigo-500/8',
    challenges: [
      'Inventory is siloed by branch — no unified view',
      "Staff at one location can't see what's available at another",
      'Different pricing at different branches causes confusion',
      'No central reporting across the whole business',
    ],
    solutions: [
      {
        icon: '🗺️',
        title: 'Unified inventory view',
        desc: 'See all stones across all locations in one dashboard.',
      },
      {
        icon: '🔄',
        title: 'Inter-branch transfers',
        desc: 'Move stones between locations with full audit trail.',
      },
      {
        icon: '👥',
        title: 'Role-based access per branch',
        desc: 'Staff see only their branch; managers see everything.',
      },
      {
        icon: '📈',
        title: 'Consolidated reporting',
        desc: 'P&L, pipeline, and inventory reports across the entire business.',
      },
    ],
    cta: 'See Multi-Location Features',
    href: '/features#inventory',
  },
];

export default function SolutionsPage() {
  return (
    <div
      className="min-h-screen bg-[#0a0e1a] text-[#e5e7eb]"
      style={{ fontFamily: 'var(--font-plus-jakarta-sans), system-ui, sans-serif' }}
    >
      <MarketingNav />
      <main id="main-content">
        {/* Hero */}
        <section className="pt-28 pb-16 px-4 sm:px-6 text-center relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-indigo-600/6 blur-[100px]" />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-indigo-500/30 bg-indigo-500/8 text-indigo-300 text-xs font-medium mb-6">
              Solutions by Business Type
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
              Built for Your Type of Diamond Business
            </h1>
            <p className="text-[#9ca3af] text-lg leading-relaxed">
              Glanz CRM adapts to how you work — whether you're a solo appraiser, a Bangkok
              retailer, or a multi-branch wholesaler.
            </p>
          </div>
        </section>
        {/* Solution cards */}
        <section className="pb-20 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto space-y-8">
            {SOLUTIONS?.map((sol, i) => (
              <div
                key={sol?.id}
                id={sol?.id}
                className={`rounded-2xl border ${sol?.border} bg-gradient-to-br ${sol?.bg} to-[#111827] overflow-hidden`}
              >
                <div className="grid lg:grid-cols-2 gap-0">
                  {/* Left: problem */}
                  <div className={`p-8 ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="flex items-center gap-3 mb-5">
                      <span className="text-3xl">{sol?.icon}</span>
                      <div>
                        <h2 className={`text-xl font-bold text-white`}>{sol?.title}</h2>
                        <p className="text-sm text-[#9ca3af]">{sol?.tagline}</p>
                      </div>
                    </div>

                    <div className="mb-6">
                      <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wide mb-3">
                        Common challenges
                      </p>
                      <ul className="space-y-2.5">
                        {sol?.challenges?.map((c) => (
                          <li key={c} className="flex items-start gap-2.5 text-sm text-[#9ca3af]">
                            <svg
                              className="w-4 h-4 text-red-400/60 flex-shrink-0 mt-0.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link
                      href={sol?.href}
                      className={`inline-flex items-center gap-1.5 text-sm font-semibold ${sol?.color} hover:opacity-80 transition-opacity`}
                    >
                      {sol?.cta}
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Link>
                  </div>

                  {/* Right: solutions */}
                  <div
                    className={`p-8 border-t lg:border-t-0 ${i % 2 === 1 ? 'lg:border-r lg:order-1' : 'lg:border-l'} border-white/5 bg-[#0d1120]/50`}
                  >
                    <p className="text-xs font-semibold text-[#9ca3af] uppercase tracking-wide mb-5">
                      How Glanz CRM solves it
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {sol?.solutions?.map((s) => (
                        <div key={s?.title} className="flex items-start gap-3">
                          <span className="text-xl flex-shrink-0">{s?.icon}</span>
                          <div>
                            <p className="text-sm font-semibold text-white">{s?.title}</p>
                            <p className="text-xs text-[#9ca3af] mt-0.5 leading-relaxed">
                              {s?.desc}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
        {/* CTA */}
        <section className="py-16 px-4 sm:px-6 bg-[#0d1120] border-t border-white/5 text-center">
          <div className="max-w-xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-4">
              Find the Right Plan for Your Business
            </h2>
            <p className="text-[#9ca3af] mb-6">
              Book a demo and we'll recommend the right setup for your specific operation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                href="/demo"
                className="px-8 py-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-[#0a0e1a] font-bold text-sm shadow-xl shadow-amber-500/20 transition-all hover:-translate-y-0.5"
              >
                Book a Demo
              </Link>
              <Link
                href="/pricing"
                className="px-8 py-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white/8 text-white font-medium text-sm transition-all hover:-translate-y-0.5"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
